// Ball-machine VOUCHER MANAGER (owner-approved mockup prototypes/kiosk-vouchers.html, 2026-10-01).
// One component, two audiences:
//   mode 'platform' → HighMark super admin: every kiosk code (its own + the courts'), machine cards for
//                     every active phone, New batch + Give a trial, extend / retire / billing statement.
//   mode 'tenant'   → the court's Vouchers page: its own batches (copy, extend, retire, New batch) plus
//                     HighMark's batches for ITS machine(s), read-only ("From HighMark", copy only) and
//                     never a HighMark figure (the server strips them — see sports/kiosk_voucher_manager.py).
// Everything is grouped by batch; a code opens a drawer with its real history (claim_history + the current
// claim + the nickname the kiosk asked). Statuses are derived here from the voucher's own fields.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  listDevices, listKioskVouchers, generateKioskVouchers, deleteKioskVoucher, deleteKioskVoucherBatch, extendKioskVoucherBatch,
  getBillingStatement, mineSummary, listTenantKioskVoucherManager, mintTenantKioskVouchers, extendTenantKioskBatch,
  deleteTenantKioskBatch, deleteTenantKioskVoucher, isAdminDenied, errMsg, ymd,
} from './kioskApi';
import { AdminDeniedNotice } from './KioskDialogs';
import { useAutoRefresh } from './useAutoRefresh';
import { buildStatementHtml } from './KioskBilling';
import KioskVoucherWizard, { GiveTrialModal, fundingLabel, courtBookingLabel } from './KioskVoucherWizard';

const TZ = 'Asia/Manila';
const MAX_STATEMENT_DAYS = 93;          // the billing statement's longest range (server MAX_RANGE_DAYS)

// ── The mockup's palette and layout, scoped to .kvm ──────────────────────────
const CSS = `
.kvm { --bg:#0B0B0D; --surface:#141417; --surface2:#1A1A1F; --line:#26262C; --line2:#33333B;
  --text:#F4F4F5; --muted:#9A9AA3; --dim:#63636C;
  --red:#FF2D55; --amber:#FFB020; --green:#34C759; --blue:#4FC3F7; --violet:#A78BFA;
  --ease:cubic-bezier(0.23, 1, 0.32, 1);
  color: var(--text); font: 14px/1.45 Inter, Manrope, system-ui, sans-serif; }
.kvm * { box-sizing: border-box; }
.kvm button { font: inherit; color: inherit; cursor: pointer; }
.kvm button:disabled { cursor: not-allowed; opacity: .45; }
.kvm :focus-visible { outline: 2px solid var(--amber); outline-offset: 2px; }
.kvm .head { display:flex; align-items:flex-end; justify-content:space-between; gap:14px; flex-wrap:wrap; }
.kvm .eyebrow { font-weight:600; font-size:11px; letter-spacing:1.4px; text-transform:uppercase; color:var(--red); }
.kvm h1, .kvm h2.h1 { font:700 30px/1.1 Oswald, sans-serif; letter-spacing:.5px; text-transform:uppercase; margin:4px 0 0; }
.kvm .sub { color:var(--muted); font-size:13px; margin-top:4px; max-width:640px; }
.kvm .actions { display:flex; gap:8px; flex-wrap:wrap; }
.kvm .btn { border:1px solid var(--line2); background:var(--surface); border-radius:12px; padding:10px 14px; font-weight:700; font-size:13px;
  display:inline-flex; align-items:center; gap:8px; transition: transform 160ms var(--ease), border-color 160ms var(--ease); }
.kvm .btn:active:not(:disabled) { transform: scale(.97); }
.kvm .btn.primary { border:0; color:#1A1200; background:linear-gradient(180deg,#FFC24D,var(--amber)); box-shadow:0 0 0 1px rgba(255,176,32,.4), 0 8px 26px -8px rgba(255,176,32,.55); }
.kvm .btn.danger { color:#FF8A80; }
@media (hover:hover) and (pointer:fine) { .kvm .btn:hover:not(:disabled) { border-color:#4A4A55; } .kvm .btn.primary:hover:not(:disabled) { filter:brightness(1.05); } }

.kvm .machines { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:10px; margin-top:20px; }
.kvm .mc { text-align:left; background:var(--surface); border:1px solid var(--line); border-radius:16px; padding:14px; position:relative; overflow:hidden;
  transition: border-color 200ms var(--ease), transform 160ms var(--ease); }
.kvm .mc:active { transform: scale(.985); }
.kvm .mc.on { border-color:var(--red); box-shadow:0 0 0 1px var(--red), 0 10px 40px -18px rgba(255,45,85,.6); }
.kvm .mc .name { font:700 15px Oswald, sans-serif; letter-spacing:.4px; text-transform:uppercase; padding-right:62px; }
.kvm .mc .where { color:var(--muted); font-size:12px; margin-top:1px; }
.kvm .mc .nums { display:flex; gap:14px; margin-top:10px; font-size:12px; color:var(--muted); }
.kvm .mc .nums b { display:block; font:700 18px Oswald, sans-serif; color:var(--text); letter-spacing:.3px; }
.kvm .mc .live { position:absolute; top:14px; right:14px; font-size:10px; font-weight:700; letter-spacing:.8px; color:var(--red); display:flex; align-items:center; gap:6px; }
.kvm .dot { width:7px; height:7px; border-radius:50%; background:var(--red); box-shadow:0 0 0 0 rgba(255,45,85,.6); animation: kvm-pulse 1.8s var(--ease) infinite; }
@keyframes kvm-pulse { 0% { box-shadow:0 0 0 0 rgba(255,45,85,.55);} 80%,100% { box-shadow:0 0 0 8px rgba(255,45,85,0);} }

.kvm .tiles { display:grid; grid-template-columns:repeat(auto-fit, minmax(170px, 1fr)); gap:10px; margin-top:14px; }
.kvm .tile { background:var(--surface); border:1px solid var(--line); border-radius:14px; padding:12px 14px; }
.kvm .tile .l { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.8px; color:var(--muted); }
.kvm .tile .v { font:700 24px Oswald, sans-serif; letter-spacing:.3px; margin-top:2px; }
.kvm .tile .s { font-size:11px; color:var(--dim); }

.kvm .toolbar { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; margin:22px 0 10px; }
.kvm .tabs { display:flex; gap:4px; background:var(--surface); border:1px solid var(--line); border-radius:12px; padding:4px; flex-wrap:wrap; }
.kvm .tab { border:0; background:transparent; padding:7px 12px; border-radius:9px; font-size:13px; font-weight:600; color:var(--muted); display:flex; gap:6px; align-items:center; }
.kvm .tab .c { font-size:11px; color:var(--dim); }
.kvm .tab.on { background:var(--surface2); color:var(--text); box-shadow: inset 0 0 0 1px var(--line2); }
.kvm .search { background:var(--surface); border:1px solid var(--line); border-radius:12px; padding:9px 12px; color:var(--text); min-width:220px; outline:none; font:13px Inter, Manrope, sans-serif; }
.kvm .search:focus { border-color:var(--red); }

.kvm .batch { background:var(--surface); border:1px solid var(--line); border-radius:16px; margin-bottom:10px; position:relative; overflow:hidden; }
.kvm .batch::before { content:""; position:absolute; left:0; top:0; bottom:0; width:5px; background:var(--rail, var(--line2)); }
.kvm .bhead { display:grid; grid-template-columns:1fr auto; gap:12px; padding:14px 16px 14px 20px; cursor:pointer; }
.kvm .btitle { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.kvm .btitle .t { font:700 17px Oswald, sans-serif; letter-spacing:.4px; text-transform:uppercase; overflow-wrap:anywhere; }
.kvm .pill { font-size:11px; font-weight:700; padding:3px 9px; border-radius:999px; border:1px solid; white-space:nowrap; display:inline-block; }
.kvm .meta { color:var(--muted); font-size:12px; margin-top:4px; display:flex; gap:6px 14px; flex-wrap:wrap; }
.kvm .meta span b { color:var(--text); font-weight:600; }
.kvm .right { text-align:right; min-width:170px; }
.kvm .big { font:700 22px Oswald, sans-serif; }
.kvm .bar { height:6px; background:var(--surface2); border-radius:99px; overflow:hidden; display:flex; margin-top:6px; }
.kvm .bar i { display:block; height:100%; }
.kvm .legend { font-size:11px; color:var(--muted); margin-top:5px; }
.kvm .chev { display:inline-block; transition: transform 220ms var(--ease); color:var(--dim); margin-left:6px; }
.kvm .batch.open .chev { transform: rotate(90deg); }
.kvm .body { border-top:1px solid var(--line); padding:10px 16px 14px 20px; animation: kvm-rise 220ms var(--ease); }
@keyframes kvm-rise { from { opacity:0; transform: translateY(-4px); } to { opacity:1; transform:none; } }
.kvm .bactions { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:10px; align-items:center; }
.kvm .bactions .btn { padding:7px 11px; font-size:12px; border-radius:10px; }
.kvm .inline { display:flex; gap:8px; align-items:center; flex-wrap:wrap; background:var(--surface2); border:1px solid var(--line2); border-radius:12px; padding:8px 10px; margin-bottom:10px; font-size:12px; color:var(--muted); }
.kvm .inline input, .kvm .inline select { background:var(--bg); border:1px solid var(--line2); color:var(--text); border-radius:9px; padding:6px 9px; font:13px Inter, Manrope, sans-serif; color-scheme: dark; }
.kvm .hint { font-size:11px; color:var(--dim); }
.kvm .tablewrap { overflow-x:auto; }
.kvm table { width:100%; border-collapse:collapse; }
.kvm th { text-align:left; font-size:11px; color:var(--dim); text-transform:uppercase; letter-spacing:.8px; font-weight:600; padding:6px 8px; border-bottom:1px solid var(--line); }
.kvm td { padding:9px 8px; border-bottom:1px solid #1E1E23; font-size:13px; vertical-align:middle; }
.kvm tr.code { cursor:pointer; transition: background 160ms var(--ease); }
@media (hover:hover) and (pointer:fine) { .kvm tr.code:hover { background:var(--surface2); } }
.kvm .mono { font:700 13px 'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas, monospace; letter-spacing:.5px; white-space:nowrap; }
.kvm .mutedtxt { color:var(--muted); }

.kvm .st-unused { color:#C9C9D1; border-color:#3A3A43; }
.kvm .st-inuse { color:var(--red); border-color:rgba(255,45,85,.5); background:rgba(255,45,85,.08); }
.kvm .st-paused { color:var(--blue); border-color:rgba(79,195,247,.45); background:rgba(79,195,247,.08); }
.kvm .st-used { color:var(--green); border-color:rgba(52,199,89,.45); background:rgba(52,199,89,.08); }
.kvm .st-back { color:#7EE0D2; border-color:rgba(126,224,210,.4); }
.kvm .st-expired { color:var(--dim); border-color:#2C2C33; }
.kvm .st-forfeit { color:#FF8A80; border-color:rgba(255,138,128,.4); }
.kvm .st-scheduled { color:var(--amber); border-color:rgba(255,176,32,.35); }
.kvm .t-trial { color:var(--blue); border-color:rgba(79,195,247,.45); }
.kvm .t-disc { color:var(--amber); border-color:rgba(255,176,32,.45); }
.kvm .t-bonus { color:var(--violet); border-color:rgba(167,139,250,.45); }
.kvm .t-court { color:#C9C9D1; border-color:#3A3A43; }
.kvm .t-hm { color:var(--red); border-color:rgba(255,45,85,.45); background:rgba(255,45,85,.06); }

.kvm .scrim { position:fixed; inset:0; background:rgba(0,0,0,.55); opacity:0; pointer-events:none; transition: opacity 220ms var(--ease); z-index:60; }
.kvm .scrim.on { opacity:1; pointer-events:auto; }
.kvm .drawer { position:fixed; top:0; right:0; bottom:0; width:min(440px, 100%); background:#111114; border-left:1px solid var(--line2);
  transform: translateX(100%); visibility:hidden; transition: transform 260ms var(--ease), visibility 0s linear 260ms; padding:20px; overflow-y:auto; z-index:61; }
.kvm .drawer.on { transform:none; visibility:visible; transition: transform 260ms var(--ease), visibility 0s; }
.kvm .x { position:absolute; top:14px; right:14px; border:1px solid var(--line2); background:var(--surface); border-radius:10px; width:34px; height:34px; }
.kvm .dcode { font:700 28px 'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas, monospace; letter-spacing:1px; margin-top:6px; overflow-wrap:anywhere; padding-right:30px; }
.kvm .facts { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin:14px 0; }
.kvm .fact { background:var(--surface); border:1px solid var(--line); border-radius:12px; padding:10px 12px; min-width:0; }
.kvm .fact .l, .kvm .seclabel { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:.7px; font-weight:600; }
.kvm .fact .v { font-weight:700; margin-top:2px; overflow-wrap:anywhere; }
.kvm .tl { position:relative; margin:6px 0 0 6px; padding-left:18px; border-left:2px solid var(--line2); }
.kvm .ev { position:relative; padding:0 0 16px; }
.kvm .ev::before { content:""; position:absolute; left:-25px; top:3px; width:10px; height:10px; border-radius:50%; background:var(--ev, var(--line2)); box-shadow:0 0 0 4px #111114; }
.kvm .ev .when { font-size:11px; color:var(--dim); }
.kvm .ev .what { font-weight:600; }
.kvm .ev .det { font-size:12px; color:var(--muted); }
.kvm .link { color:var(--amber); font-weight:600; font-size:12px; background:none; border:0; padding:0; text-align:left; }
.kvm .empty { text-align:center; color:var(--muted); padding:40px; border:1px dashed var(--line2); border-radius:16px; }
.kvm .confirm { position:fixed; inset:0; z-index:70; background:rgba(0,0,0,.7); display:flex; align-items:center; justify-content:center; padding:16px; }
.kvm .confirm .card { background:#111114; border:1px solid var(--line2); border-radius:16px; padding:20px; width:100%; max-width:420px; }
.kvm .confirm h3 { font:700 18px Oswald, sans-serif; text-transform:uppercase; letter-spacing:.4px; margin:0 0 6px; }

@media (max-width: 640px) {
  .kvm h1, .kvm h2.h1 { font-size:24px; }
  .kvm .bhead { grid-template-columns:1fr; }
  .kvm .right { text-align:left; min-width:0; }
  .kvm .hide-sm { display:none; }
  .kvm .search { min-width:0; width:100%; }
  .kvm .facts { grid-template-columns:1fr; }
  .kvm .tiles { grid-template-columns:1fr 1fr; }
  .kvm .tile .v { font-size:20px; }
}
@media (prefers-reduced-motion: reduce) {
  .kvm .dot { animation:none; }
  .kvm *, .kvm .drawer, .kvm .scrim { transition:none !important; animation:none !important; }
}
`;

// ── Dates (Asia/Manila) ──────────────────────────────────────────────────────
const dtf = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: TZ, ...opts });
const DAY_FMT = dtf({ month: 'short', day: 'numeric' });
const TIME_FMT = dtf({ hour: 'numeric', minute: '2-digit' });
const MONTH_FMT = dtf({ month: 'short' });
const YMD_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const ymdOfMs = (ms) => YMD_FMT.format(new Date(ms));
const todayManila = () => ymdOfMs(Date.now());
const msOf = (x) => {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x !== 'string' || !x) return null;
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(x) ? x : `${x}Z`);   // server ISO strings are UTC
  return Number.isFinite(t) ? t : null;
};
const fmtDT = (ms) => (ms ? `${DAY_FMT.format(new Date(ms))} · ${TIME_FMT.format(new Date(ms))}` : '—');
const fmtTime = (ms) => (ms ? TIME_FMT.format(new Date(ms)) : '');
const fmtDayMs = (ms) => (ms ? DAY_FMT.format(new Date(ms)) : '');
const fmtDay = (iso) => {
  if (!iso || typeof iso !== 'string') return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const daysBetween = (a, b) => {
  const [y1, m1, d1] = a.split('-').map(Number);
  const [y2, m2, d2] = b.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};
const peso = (c) => {
  const v = (Number(c) || 0) / 100;
  return `₱${v.toLocaleString('en-PH', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};

// ── Words ────────────────────────────────────────────────────────────────────
const STOP_WORD = {
  WEATHER: 'rain', EMERGENCY: 'emergency', MACHINE_FAULT: 'machine problem', KIOSK_FAULT: 'kiosk problem',
  COURT_CLOSED: 'court closed', MISTAKE: 'started by mistake', OUTAGE: 'power or internet outage',
  CANCELLED: 'cancelled before starting', REMOVED: 'code removed before starting', VOID: 'rental voided',
};
const stopWord = (r) => STOP_WORD[String(r || '').toUpperCase()] || (r ? String(r).toLowerCase().replace(/_/g, ' ') : 'given back');

const ST = {
  unused: ['Unused', 'st-unused'], inuse: ['In use', 'st-inuse'], paused: ['Paused', 'st-paused'], used: ['Used', 'st-used'],
  back: ['Given back', 'st-back'], expired: ['Expired', 'st-expired'], forfeit: ['Ended by player', 'st-forfeit'],
  scheduled: ['Not started', 'st-scheduled'],
};
const KIND = { trial: ['Free trial', 't-trial', '#4FC3F7'], disc: ['Discount', 't-disc', '#FFB020'], bonus: ['Free machine time', 't-bonus', '#A78BFA'] };
const TABS = [['active', 'Active'], ['paused', 'Paused trials'], ['used', 'Used'], ['expired', 'Expired'], ['all', 'Everything']];
const READY = new Set(['unused', 'back', 'scheduled']);
const kindOf = (t) => (t === 'trial' ? 'trial' : t === 'bonus_time' ? 'bonus' : 'disc');
const num = (x) => Math.max(0, Math.floor(Number(x) || 0));

function typeLabel(v) {
  const val = Number(v?.value) || 0;
  switch (v?.type) {
    case 'trial': return `Free trial · ${num(val)} min`;
    case 'bonus_time': return `Free machine time · +${num(val)} min`;
    case 'flat': return `₱${val % 1 ? val.toFixed(2) : val} off`;
    case 'percent': return `${val}% off`;
    case 'free_time': return `${num(val)} min off`;
    default: return v?.label || 'Voucher';
  }
}

// The state a code is in, for the pills, the counts and the tabs.
export function codeState(v, today, now = Date.now()) {
  if (v.type === 'trial' && v.trial_ended) return v.trial_ended === 'forfeited' ? 'forfeit' : 'used';
  if (v.used) return v.session_state === 'CONSUMED' ? 'used' : 'inuse';
  if (v.type === 'trial' && v.trial_paused) {
    const exp = Number(v.trial_expires_at_ms);
    return exp && exp <= now ? 'expired' : 'paused';
  }
  if (v.valid_until && v.valid_until < today) return 'expired';
  if (v.valid_from && v.valid_from > today) return 'scheduled';
  if (v.released_at) return 'back';
  return 'unused';
}

// The machine a code was (last) used on — '' when it never was.
function codeMachine(v) {
  if (v.claimed_by_kiosk_id && (v.used || v.trial_paused || v.released_at)) return v.claimed_by_kiosk_id;
  const h = (v.claim_history || []).filter(x => x && x.kiosk_id);
  return h.length ? h[h.length - 1].kiosk_id : (v.claimed_by_kiosk_id || '');
}

function payShort(v, mode) {
  const f = v.funding;
  if (mode === 'tenant' && !v.from_highmark) return 'court-paid';
  if (f === 'court') return 'court-paid';
  if (f === 'owner') return 'HighMark-paid';
  if (f === 'shared') return 'shared split';
  if (mode === 'tenant') return 'split';
  const c = Number(v.court_pct ?? 100);
  return c >= 100 ? 'court-paid' : c <= 0 ? 'HighMark-paid' : `court ${c}% · HighMark ${100 - c}%`;
}

function codeDetail(v, st, mode) {
  const total = num(v.value);
  const usedMin = num(v.trial_used_minutes);
  const parts = num(v.trial_parts);
  const disc = num(v.claim_result?.discount_centavos);
  if (st === 'inuse') {
    if (v.type === 'trial') return `Part ${parts + 1} · running`;
    if (v.type === 'bonus_time') return `+${total} min free · running`;
    return disc ? `−${peso(disc)} locked · running` : 'Running';
  }
  if (st === 'paused') {
    const until = fmtDayMs(Number(v.trial_expires_at_ms));
    return `${Math.max(total - usedMin, 0)} of ${total} min left${until ? ` · until ${until}` : ''}`;
  }
  if (st === 'used') {
    if (v.type === 'trial') return `${usedMin || total} of ${total} min${parts > 1 ? ` · ${parts} parts` : ''}`;
    if (v.type === 'bonus_time') return `+${total} min free`;
    return `−${peso(disc)} · ${payShort(v, mode)}`;
  }
  if (st === 'forfeit') return `Ended by the player at ${usedMin} min`;
  if (st === 'back') return `Given back · ${stopWord(v.release_reason)}`;
  if (st === 'expired') {
    if (v.type === 'trial' && v.trial_paused) return `${Math.max(total - usedMin, 0)} min not used · ended ${fmtDayMs(Number(v.trial_expires_at_ms))}`;
    return `Expired ${fmtDay(v.valid_until)}`;
  }
  if (st === 'scheduled') return `Starts ${fmtDay(v.valid_from)}`;
  return '';
}

function codeWhen(v, st) {
  if (st === 'inuse') { const t = v.claim_started_at_ms || v.claimed_at_ms; return t ? `Now · since ${fmtTime(t)}` : 'Now'; }
  if (st === 'used' || st === 'forfeit') return fmtDT(v.claim_started_at_ms || v.claimed_at_ms);
  if (st === 'paused' || st === 'back') return fmtDT(v.released_at_ms || msOf(v.released_at));
  return '—';
}

// The drawer's history, from the voucher's own data only.
function buildHistory(v, b, st, mode) {
  const total = num(v.value);
  const trial = v.type === 'trial';
  const ev = [];
  ev.push(['#63636C', fmtDT(msOf(v.created_at)), 'Created', `${b.title} · ${typeLabel(v)} · by ${b.maker}`]);
  const history = (v.claim_history || []).filter(Boolean);
  let left = total;
  history.forEach(h => {
    const who = [h.customer_name, h.kiosk_name].filter(Boolean).join(' · ');
    const start = h.started_at_ms || (h.claim_id === v.claim_id ? (v.claim_started_at_ms || v.claimed_at_ms) : null);
    if (start) ev.push(['#FF2D55', fmtDT(start), trial && h.part ? `Part ${h.part} started` : 'Started', who]);
    const when = fmtDT(msOf(h.released_at));
    const played = num(h.played_minutes);
    const lead = start ? '' : (who ? `${who} · ` : '');
    switch (h.outcome) {
      case 'paused':
        left = Math.max(left - played, 0);
        ev.push(['#4FC3F7', when, `Paused · ${stopWord(h.release_reason)}`, `${lead}${played} of ${total} min played · ${left} min kept on the code`]);
        break;
      case 'finished':
        ev.push(['#34C759', when, 'Finished', `${lead}${played} min played${h.part ? ` in part ${h.part}` : ''} · ₱0 for everyone`]);
        break;
      case 'forfeited':
        ev.push(['#FF8A80', when, 'Ended by the player', `${lead}${played} min played · the rest is lost`]);
        break;
      case 'backed_out':
        ev.push(['#7EE0D2', when, 'Given back before starting', `${lead}0 min played · the code works again`]);
        break;
      default:
        ev.push(['#7EE0D2', when, `Given back · ${stopWord(h.release_reason)}`, `${lead}The code works again`]);
    }
  });
  const inHistory = v.claim_id && history.some(h => h.claim_id === v.claim_id);
  if (v.used && v.claim_id && !inHistory) {
    const who = [v.used_by_name, v.claimed_device_name].filter(Boolean).join(' · ');
    const booking = courtBookingLabel(v);
    const start = v.claim_started_at_ms || v.claimed_at_ms;
    ev.push(['#FF2D55', fmtDT(start), trial ? `Part ${num(v.trial_parts) + 1} started` : 'Started', [who, booking].filter(Boolean).join(' · ')]);
    if (st === 'used') ev.push(['#34C759', fmtDT(v.claim_ended_at_ms), 'Used', codeDetail(v, st, mode)]);
    else ev.push(['#33333B', 'Now', 'In use', 'The code is held by this rental until it ends']);
  }
  if (st === 'paused') ev.push(['#33333B', `Until ${fmtDayMs(Number(v.trial_expires_at_ms))}`, 'Waiting to continue', 'Enter the code again on an allowed machine']);
  if (READY.has(st)) ev.push(['#33333B', fmtDay(v.valid_until), 'Expires if unused', st === 'scheduled' ? `Works from ${fmtDay(v.valid_from)}` : '']);
  if (st === 'expired') {
    ev.push(['#33333B', v.trial_paused ? fmtDayMs(Number(v.trial_expires_at_ms)) : fmtDay(v.valid_until),
      v.trial_paused ? 'Expired · minutes not used' : 'Expired unused', '']);
  }
  return ev;
}

function copyText(text, what) {
  const done = () => toast.success(what);
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => toast.error('Could not copy'));
  else toast.error('Copying is not available in this browser');
}

// ── The component ────────────────────────────────────────────────────────────
export default function KioskVoucherManager({ mode = 'platform', courtName = '' }) {
  const platform = mode === 'platform';
  const [vouchers, setVouchers] = useState([]);
  const [devices, setDevices] = useState([]);           // { id, name, where, site_name, court_name, live }
  const [loaded, setLoaded] = useState(false);
  const [denied, setDenied] = useState(false);
  const [machine, setMachine] = useState(null);         // null until the devices arrive
  const [tab, setTab] = useState('active');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [firstOpened, setFirstOpened] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOn, setDrawerOn] = useState(false);
  const [extendFor, setExtendFor] = useState(null);     // batch id with the extend row open
  const [extendDate, setExtendDate] = useState('');
  const [pickFor, setPickFor] = useState(null);         // batch id asking which machine's statement
  const [confirm, setConfirm] = useState(null);         // { title, body, action, label }
  const [busy, setBusy] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [giveTrial, setGiveTrial] = useState(false);
  const closeRef = useRef(null);
  const today = todayManila();

  const load = useCallback((silent = false) => {
    const work = platform
      ? Promise.all([listKioskVouchers({}), listDevices().catch(() => null)]).then(([v, d]) => {
        const list = Array.isArray(d) ? d : (d?.devices || []);
        setVouchers(v?.vouchers || []);
        setDevices(list.filter(x => x.status === 'active').map(x => ({
          id: x.kiosk_id || x.id, name: x.name || 'Machine', site_name: x.site_name || '', court_name: x.court_name || '',
          where: x.court_name || x.site_name || 'Not linked to a court', live: !!x.live,
        })));
      })
      : Promise.all([listTenantKioskVoucherManager(), mineSummary({ period: 'day', anchor: ymd() }).catch(() => null)]).then(([v, d]) => {
        setVouchers(Array.isArray(v) ? v : []);
        setDevices((d?.devices || []).filter(x => x.status === 'active').map(x => ({
          id: x.kiosk_id, name: x.name || 'Machine', site_name: x.site_name || '', court_name: courtName,
          where: x.site_name || courtName || 'Your court', live: !!x.in_session,
        })));
      });
    return work
      .then(() => setDenied(false))
      .catch(e => { if (platform && isAdminDenied(e)) setDenied(true); else if (!silent) toast.error(errMsg(e, 'Could not load the vouchers')); })
      .finally(() => setLoaded(true));
  }, [platform, courtName]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(true));

  // Machine cards: "All machines" + every active machine (the court: only when it has more than one).
  const machines = useMemo(() => {
    const all = { id: 'all', name: 'All machines', where: platform ? 'Every court' : 'Every machine of yours' };
    if (!platform && devices.length === 1) return devices;
    return [all, ...devices];
  }, [devices, platform]);
  useEffect(() => {
    if (machine && machines.some(m => m.id === machine)) return;
    if (machines.length) setMachine(machines[0].id);
  }, [machines, machine]);
  const sel = machine || 'all';
  const devById = useMemo(() => Object.fromEntries(devices.map(d => [d.id, d])), [devices]);

  // Codes → batches.
  const batches = useMemo(() => {
    const map = new Map();
    const now = Date.now();
    vouchers.forEach(v => {
      const id = v.batch_id || `single-${v.id}`;
      if (!map.has(id)) map.set(id, { id, codes: [] });
      map.get(id).codes.push({ ...v, _st: codeState(v, today, now), _m: codeMachine(v) });
    });
    return [...map.values()].map(b => {
      const f = b.codes[0];
      const kind = kindOf(f.type);
      const tenantMade = (f.minted_by || (f.organization_id === '__platform__' ? 'platform' : 'tenant')) === 'tenant';
      const fromHighmark = !platform && !!f.from_highmark;
      const maker = platform ? (tenantMade ? (f.org_name || 'A court') : 'HighMark') : (fromHighmark ? 'HighMark' : (courtName || 'Your court'));
      const deviceIds = f.device_ids || [];
      const siteNames = f.site_names || [];
      const names = deviceIds.map((d, i) => (f.device_names || [])[i] || devById[d]?.name || d);
      let scope = 'Any kiosk';
      if (names.length || siteNames.length) {
        scope = names.length === 1 && !siteNames.length ? `${names[0]} only` : [names.join(', '), siteNames.length ? `sites: ${siteNames.join(', ')}` : ''].filter(Boolean).join(' · ');
      }
      let payer;
      if (fromHighmark) payer = f.who_pays || fundingLabel(f);
      else if (kind !== 'disc') payer = 'Nobody pays · ₱0';
      else payer = fundingLabel(f);
      const froms = b.codes.map(c => c.valid_from).filter(Boolean).sort();
      const untils = b.codes.map(c => c.valid_until).filter(Boolean).sort();
      const n = (s) => b.codes.filter(c => c._st === s).length;
      const stats = {
        total: b.codes.length, used: n('used') + n('forfeit'), inuse: n('inuse'), paused: n('paused'), expired: n('expired'),
        left: n('unused') + n('back') + n('scheduled'),
      };
      return {
        ...b, first: f, kind, tenantMade, fromHighmark, readOnly: !!f.read_only || fromHighmark, maker, scope, payer, deviceIds, siteNames,
        title: (f.reason || f.batch_label || f.label || typeLabel(f)).trim(), typeText: typeLabel(f),
        validFrom: froms[0] || '', validUntil: untils[untils.length - 1] || '', stats,
      };
    });
  }, [vouchers, today, platform, courtName, devById]);

  const onMachine = useCallback((b, mid) => {
    if (mid === 'all') return true;
    if (b.deviceIds.includes(mid)) return true;
    if (!b.deviceIds.length) {
      if (!b.siteNames.length) return true;
      const site = (devById[mid]?.site_name || '').toLowerCase();
      if (site && b.siteNames.some(s => String(s).toLowerCase() === site)) return true;
    }
    return b.codes.some(c => c._m === mid);
  }, [devById]);
  const codeOnMachine = (c, mid) => mid === 'all' || !c._m || c._m === mid;

  const tabTest = (b, t) => {
    const s = b.stats;
    if (t === 'all') return true;
    if (t === 'active') return s.left + s.paused + s.inuse > 0;
    if (t === 'paused') return s.paused > 0;
    if (t === 'used') return s.used > 0;
    return s.expired > 0;
  };
  const query = q.trim().toLowerCase();
  const searchTest = (b) => {
    if (!query) return true;
    if ([b.title, b.first.label, b.first.batch_label, b.first.reason, b.typeText].some(x => String(x || '').toLowerCase().includes(query))) return true;
    return b.codes.some(c => String(c.code || '').toLowerCase().includes(query)
      || String(c.used_by_name || '').toLowerCase().includes(query)
      || (c.claim_history || []).some(h => String(h?.customer_name || '').toLowerCase().includes(query)));
  };

  const inSel = useMemo(() => batches.filter(b => onMachine(b, sel)), [batches, sel, onMachine]);
  const shown = inSel.filter(b => tabTest(b, tab)).filter(searchTest);

  // Open the first batch once, the way the mockup lands.
  useEffect(() => {
    if (!firstOpened && shown.length) { setOpen(new Set([shown[0].id])); setFirstOpened(true); }
  }, [shown, firstOpened]);

  // Machine cards' numbers.
  const machineNums = (mid) => {
    const bs = batches.filter(b => onMachine(b, mid));
    const codes = bs.flatMap(b => b.codes).filter(c => mid === 'all' || c._m === mid);
    return {
      ready: bs.reduce((a, b) => a + b.stats.left, 0),
      used: codes.filter(c => c._st === 'used' || c._st === 'forfeit').length,
      paused: codes.filter(c => c._st === 'paused').length,
    };
  };

  // Tiles for the selected machine.
  const tiles = useMemo(() => {
    const codes = inSel.flatMap(b => b.codes.filter(c => codeOnMachine(c, sel)));
    const trialsReady = inSel.filter(b => b.kind === 'trial').reduce((a, b) => a + b.codes.filter(c => READY.has(c._st)).length, 0);
    const paused = codes.filter(c => c._st === 'paused');
    const pausedMin = paused.reduce((a, c) => a + Math.max(num(c.value) - num(c.trial_used_minutes), 0), 0);
    const month = today.slice(0, 7);
    const disc = codes.filter(c => c._st === 'used' && kindOf(c.type) === 'disc' && (c.claimed_at_ms ? ymdOfMs(c.claimed_at_ms) : String(c.claimed_at || '')).slice(0, 7) === month);
    let total = 0; let court = 0; let hm = 0; let shared = 0; let mine = 0; let theirs = 0;
    disc.forEach(c => {
      const d = num(c.claim_result?.discount_centavos);
      total += d;
      if (!platform) { if (c.from_highmark) theirs += d; else mine += d; return; }
      if (c.funding === 'shared') shared += d;
      else { court += num(c.claim_court_funded_centavos); hm += num(c.claim_owner_funded_centavos); }
    });
    const soon = addDays(today, 7);
    const expiring = codes.filter(c => (c._st === 'unused' || c._st === 'back') && c.valid_until && c.valid_until >= today && c.valid_until <= soon).length;
    const discSub = platform
      ? `court-paid ${peso(court)} · HighMark ${peso(hm)}${shared ? ` · shared ${peso(shared)}` : ''}`
      : `your batches ${peso(mine)} · HighMark's ${peso(theirs)}`;
    return { trialsReady, paused: paused.length, pausedMin, total, discSub, expiring, monthName: MONTH_FMT.format(new Date()) };
  }, [inSel, sel, today, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggle = (id) => setOpen(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const copyUnused = (b) => {
    const codes = b.codes.filter(c => READY.has(c._st)).map(c => c.code);
    if (!codes.length) return;
    copyText(codes.join('\n'), `Copied ${codes.length} unused code${codes.length === 1 ? '' : 's'}`);
  };

  const extendable = (b) => b.codes.filter(c => !c.used && !c.trial_paused && !c.trial_ended).length;
  const startExtend = (b) => {
    const base = b.validUntil && b.validUntil > today ? b.validUntil : today;
    setExtendDate(addDays(base, 7));
    setExtendFor(extendFor === b.id ? null : b.id);
    setPickFor(null);
  };
  const doExtend = async (b) => {
    if (!extendDate || extendDate < today) { toast.error('Pick a date from today on'); return; }
    setBusy(true);
    try {
      const r = platform ? await extendKioskVoucherBatch(b.id, extendDate) : await extendTenantKioskBatch(b.id, extendDate);
      toast.success(`${r.updated} unused code${r.updated === 1 ? '' : 's'} now valid until ${fmtDay(r.valid_until)}`);
      setExtendFor(null);
      load(true);
    } catch (e) { toast.error(errMsg(e, 'Could not extend the batch')); }
    finally { setBusy(false); }
  };

  const retireBatch = (b) => {
    const n = b.codes.filter(c => READY.has(c._st) || (c._st === 'expired' && !c.trial_paused)).length;
    setConfirm({
      title: 'Retire unused codes',
      body: `Delete the ${n} unused code${n === 1 ? '' : 's'} of “${b.title}”? Anyone holding one won't be able to use it. Used, running and paused codes stay on record.`,
      label: `Retire ${n} code${n === 1 ? '' : 's'}`,
      action: async () => {
        const r = platform ? await deleteKioskVoucherBatch(b.id) : await deleteTenantKioskBatch(b.id);
        toast.success(`Retired ${r.deleted} unused code${r.deleted === 1 ? '' : 's'}`);
      },
    });
  };
  const retireCode = (c) => {
    setConfirm({
      title: 'Retire this code',
      body: `Delete ${c.code}? Whoever holds it won't be able to use it.`,
      label: 'Retire code',
      action: async () => {
        if (platform) await deleteKioskVoucher(c.id); else await deleteTenantKioskVoucher(c.id);
        toast.success(`${c.code} retired`);
        setDrawerOn(false);
      },
    });
  };
  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try { await confirm.action(); setConfirm(null); load(true); }
    catch (e) { toast.error(errMsg(e, 'Could not do that')); }
    finally { setBusy(false); }
  };

  // Billing statement (HighMark's document): one machine, a date range. Opened inside the click so no
  // popup blocker stops it.
  const openStatement = async (kioskId, from, to) => {
    const win = window.open('', '_blank');
    if (!win) { toast.error('Allow pop-ups for this site to open the statement.'); return; }
    win.document.write('<p style="font-family:Arial;padding:20px">Building the statement…</p>');
    try {
      const s = await getBillingStatement(kioskId, { period: 'range', from, to });
      win.document.open(); win.document.write(buildStatementHtml(s)); win.document.close();
    } catch (e) { win.close(); toast.error(errMsg(e, 'Could not build the statement')); }
  };
  const statementRange = (b) => {
    const to = b.validUntil && b.validUntil < today ? b.validUntil : today;
    let from = b.validFrom || to;
    if (from > to) return null;
    if (daysBetween(from, to) + 1 > MAX_STATEMENT_DAYS) from = addDays(to, -(MAX_STATEMENT_DAYS - 1));
    return { from, to };
  };
  const statementMachines = (b) => (b.deviceIds.length ? b.deviceIds.filter(id => devById[id]) : devices.map(d => d.id));
  const batchStatement = (b) => {
    const range = statementRange(b);
    if (!range) return;
    const cands = statementMachines(b);
    const pick = sel !== 'all' && cands.includes(sel) ? sel : (cands.length === 1 ? cands[0] : null);
    if (pick) { setPickFor(null); openStatement(pick, range.from, range.to); return; }
    setExtendFor(null);
    setPickFor(pickFor === b.id ? null : b.id);
  };

  // Drawer
  const drawerCode = useMemo(() => {
    if (!drawerId) return null;
    for (const b of batches) { const c = b.codes.find(x => x.id === drawerId); if (c) return { c, b }; }
    return null;
  }, [drawerId, batches]);
  const showCode = (c) => { setDrawerId(c.id); setDrawerOn(true); };
  useEffect(() => {
    if (!drawerOn) return undefined;
    const t = setTimeout(() => closeRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOn(false); };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [drawerOn]);

  // ── Render ────────────────────────────────────────────────────────────────
  const wizardDevices = devices.map(d => ({ kiosk_id: d.id, name: d.name, site_name: d.site_name, court_name: d.court_name }));
  const noMachine = !platform && loaded && devices.length === 0;
  const HeadTag = platform ? 'h1' : 'h2';

  const head = (
    <div className="head">
      <div>
        <div className="eyebrow">{platform ? 'HighMark · Ball machines' : `${courtName || 'Your court'} · Ball machine`}</div>
        <HeadTag className="h1">{platform ? 'Vouchers' : 'Ball machine vouchers'}</HeadTag>
        <div className="sub">
          {platform
            ? 'Every code by machine and by batch — who used it, when, and what it cost.'
            : 'Codes your customers type on the rental kiosk — yours, and the ones HighMark made for your machine. Your vouchers come out of your share; for a joint promo, contact HighMark.'}
        </div>
      </div>
      <div className="actions">
        <button type="button" className={platform ? 'btn' : 'btn primary'} onClick={() => setWizard(true)} disabled={denied || noMachine}
          title={noMachine ? 'No rental kiosk is linked to your court' : ''} data-testid={platform ? 'kiosk-voucher-create-btn' : 'bm-voucher-create-btn'}>
          ＋ New batch
        </button>
        {platform && (
          <button type="button" className="btn primary" onClick={() => setGiveTrial(true)} disabled={denied} data-testid="kiosk-give-trial-btn">
            ▶ Give a trial
          </button>
        )}
      </div>
    </div>
  );

  if (denied) return <div className="kvm"><style>{CSS}</style>{head}<div style={{ marginTop: 20 }}><AdminDeniedNotice /></div></div>;

  return (
    <div className="kvm" data-testid={`kiosk-voucher-manager-${mode}`}>
      <style>{CSS}</style>
      {head}

      {machines.length > 0 && (
        <div className="machines" role="group" aria-label="Machines">
          {machines.map(m => {
            const n = machineNums(m.id);
            return (
              <button type="button" key={m.id} className={`mc ${sel === m.id ? 'on' : ''}`} aria-pressed={sel === m.id}
                onClick={() => setMachine(m.id)} data-testid={`kvm-machine-${m.id}`}>
                {m.live && <span className="live"><span className="dot" />IN USE</span>}
                <div className="name">{m.name}</div><div className="where">{m.where}</div>
                <div className="nums"><div><b>{n.ready}</b>ready</div><div><b>{n.used}</b>used</div><div><b>{n.paused}</b>paused</div></div>
              </button>
            );
          })}
        </div>
      )}

      <div className="tiles">
        <div className="tile"><div className="l">Trial codes ready</div><div className="v" style={{ color: 'var(--blue)' }} data-testid="kvm-tile-trials">{tiles.trialsReady}</div><div className="s">to hand out</div></div>
        <div className="tile"><div className="l">Paused trials</div><div className="v" data-testid="kvm-tile-paused">{tiles.paused}</div><div className="s">{tiles.paused ? `${tiles.pausedMin} min still on the codes` : 'minutes still on the code'}</div></div>
        <div className="tile"><div className="l">Discounts used · {tiles.monthName}</div><div className="v" style={{ color: 'var(--amber)' }} data-testid="kvm-tile-discounts">{tiles.total ? `−${peso(tiles.total)}` : '₱0'}</div><div className="s">{tiles.discSub}</div></div>
        <div className="tile"><div className="l">Expiring this week</div><div className="v" data-testid="kvm-tile-expiring">{tiles.expiring}</div><div className="s">unused codes</div></div>
      </div>

      <div className="toolbar">
        <div className="tabs" role="tablist" aria-label="Which vouchers">
          {TABS.map(([id, label]) => (
            <button type="button" key={id} role="tab" aria-selected={tab === id} className={`tab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)} data-testid={`kvm-tab-${id}`}>
              {label} <span className="c">{inSel.filter(b => tabTest(b, id)).length}</span>
            </button>
          ))}
        </div>
        <input className="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search a code, batch or nickname…" aria-label="Search a code, batch or nickname" data-testid="kvm-search" />
      </div>

      {!loaded ? (
        <div className="empty">Loading…</div>
      ) : noMachine ? (
        <div className="empty" data-testid="bm-vouchers-empty">No rental kiosk is linked to your court yet.</div>
      ) : shown.length === 0 ? (
        <div className="empty" data-testid="kiosk-vouchers-empty">
          {query ? 'Nothing matches that search.' : platform ? 'No vouchers here. Give a trial or make a batch.' : 'No vouchers here. Make a batch.'}
        </div>
      ) : shown.map(b => {
        const s = b.stats;
        const pct = (x) => `${((100 * x) / Math.max(s.total, 1)).toFixed(1)}%`;
        const isOpen = open.has(b.id);
        const codes = b.codes.filter(c => codeOnMachine(c, sel));
        const range = statementRange(b);
        const cands = statementMachines(b);
        const nExt = extendable(b);
        const nRetire = b.codes.filter(c => READY.has(c._st) || (c._st === 'expired' && !c.trial_paused)).length;
        return (
          <div key={b.id} className={`batch ${isOpen ? 'open' : ''}`} style={{ '--rail': KIND[b.kind][2] }} data-testid={`kvm-batch-${b.id}`}>
            <div className="bhead" role="button" tabIndex={0} aria-expanded={isOpen} onClick={() => toggle(b.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(b.id); } }}>
              <div style={{ minWidth: 0 }}>
                <div className="btitle">
                  <span className="t">{b.title}</span>
                  <span className={`pill ${KIND[b.kind][1]}`}>{b.typeText}</span>
                  {platform && b.tenantMade && <span className="pill t-court">Made by the court</span>}
                  {b.fromHighmark && <span className="pill t-hm">From HighMark</span>}
                </div>
                <div className="meta">
                  <span>Made by <b>{b.maker}</b></span>
                  <span>Works on <b>{b.scope}</b></span>
                  <span>Valid <b>{fmtDay(b.validFrom)} → {fmtDay(b.validUntil)}</b></span>
                  <span className="hide-sm">{b.payer}</span>
                </div>
              </div>
              <div className="right">
                <div className="big">{s.used}<span className="mutedtxt" style={{ fontSize: 14 }}> / {s.total} used</span><span className="chev" aria-hidden="true">›</span></div>
                <div className="bar" aria-hidden="true">
                  <i style={{ width: pct(s.used), background: 'var(--green)' }} /><i style={{ width: pct(s.inuse), background: 'var(--red)' }} />
                  <i style={{ width: pct(s.paused), background: 'var(--blue)' }} /><i style={{ width: pct(s.expired), background: '#2C2C33' }} />
                </div>
                <div className="legend">{s.left} ready{s.paused ? ` · ${s.paused} paused` : ''}{s.inuse ? ` · ${s.inuse} in use` : ''}{s.expired ? ` · ${s.expired} expired` : ''}</div>
              </div>
            </div>
            {isOpen && (
              <div className="body">
                <div className="bactions">
                  <button type="button" className="btn" onClick={() => copyUnused(b)} disabled={!s.left} data-testid={`kvm-copy-${b.id}`}>
                    Copy {s.left} unused code{s.left === 1 ? '' : 's'}
                  </button>
                  {!b.readOnly && (
                    <button type="button" className="btn" onClick={() => startExtend(b)} disabled={!nExt} data-testid={`kvm-extend-${b.id}`}
                      title={nExt ? '' : 'No unused codes to extend'}>
                      Extend{extendFor === b.id ? '…' : ''}
                    </button>
                  )}
                  {platform && (
                    <button type="button" className="btn" onClick={() => batchStatement(b)} disabled={!range || !cands.length} data-testid={`kvm-statement-${b.id}`}
                      title={!range ? 'This batch has not started yet' : !cands.length ? 'None of its machines is active' : ''}>
                      Billing statement for this batch
                    </button>
                  )}
                  {!b.readOnly && (
                    <button type="button" className="btn danger" onClick={() => retireBatch(b)} disabled={!nRetire} data-testid={`kvm-retire-${b.id}`}>
                      Retire unused
                    </button>
                  )}
                  {b.readOnly && <span className="hint">HighMark&apos;s batch — you can copy its codes; only HighMark can change it.</span>}
                </div>
                {extendFor === b.id && (
                  <div className="inline" data-testid={`kvm-extend-row-${b.id}`}>
                    <label htmlFor={`ext-${b.id}`}>New end date for the {nExt} unused code{nExt === 1 ? '' : 's'}</label>
                    <input id={`ext-${b.id}`} type="date" min={today} value={extendDate} onChange={e => setExtendDate(e.target.value)} />
                    <button type="button" className="btn primary" style={{ padding: '6px 11px', fontSize: 12 }} disabled={busy || !extendDate || extendDate < today}
                      onClick={() => doExtend(b)} data-testid={`kvm-extend-save-${b.id}`}>
                      {busy ? 'Saving…' : `Extend to ${fmtDay(extendDate)}`}
                    </button>
                    <button type="button" className="btn" style={{ padding: '6px 11px', fontSize: 12 }} onClick={() => setExtendFor(null)}>Cancel</button>
                    <span className="hint" style={{ flexBasis: '100%' }}>Used, running and paused codes keep their dates.</span>
                  </div>
                )}
                {pickFor === b.id && range && (
                  <div className="inline" data-testid={`kvm-statement-pick-${b.id}`}>
                    <label htmlFor={`pick-${b.id}`}>{b.deviceIds.length ? 'This batch works on several machines — which one?' : 'Any kiosk — which machine’s statement?'}</label>
                    <select id={`pick-${b.id}`} defaultValue="" onChange={e => { if (e.target.value) { openStatement(e.target.value, range.from, range.to); setPickFor(null); } }}>
                      <option value="" disabled>Pick a machine</option>
                      {cands.map(id => <option key={id} value={id}>{devById[id]?.name || id}{devById[id]?.where ? ` · ${devById[id].where}` : ''}</option>)}
                    </select>
                    <span className="hint" style={{ flexBasis: '100%' }}>{fmtDay(range.from)} → {fmtDay(range.to)}{range.from !== b.validFrom ? ' (the last 93 days — the longest statement)' : ''}</span>
                  </div>
                )}
                {codes.length === 0 ? (
                  <div className="hint" style={{ padding: '8px 0' }}>No code of this batch was used on this machine.</div>
                ) : (
                  <div className="tablewrap">
                    <table>
                      <thead><tr><th>Code</th><th>Status</th><th>Used by</th><th className="hide-sm">Machine</th><th>When</th><th className="hide-sm">Detail</th></tr></thead>
                      <tbody>
                        {codes.map(c => {
                          const [sl, sc] = ST[c._st];
                          return (
                            <tr key={c.id} className="code" tabIndex={0} onClick={() => showCode(c)} data-testid={`kvm-code-${c.code}`}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showCode(c); } }}>
                              <td className="mono">{c.code}</td>
                              <td><span className={`pill ${sc}`}>{c._st === 'inuse' ? '● ' : ''}{sl}</span></td>
                              <td>{c.used_by_name || <span className="mutedtxt">—</span>}</td>
                              <td className="hide-sm mutedtxt">{c._m ? (c.claimed_device_name && c._m === c.claimed_by_kiosk_id ? c.claimed_device_name : (devById[c._m]?.name || 'Another machine')) : '—'}</td>
                              <td className="mutedtxt">{codeWhen(c, c._st)}</td>
                              <td className="hide-sm mutedtxt">{codeDetail(c, c._st, mode)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* The code drawer */}
      <div className={`scrim ${drawerOn ? 'on' : ''}`} onClick={() => setDrawerOn(false)} aria-hidden="true" />
      <aside className={`drawer ${drawerOn ? 'on' : ''}`} aria-hidden={!drawerOn} role="dialog" aria-modal="true" aria-label="Voucher code" data-testid="kvm-drawer">
        {drawerCode && (() => {
          const { c, b } = drawerCode;
          const [sl, sc] = ST[c._st];
          const ev = buildHistory(c, b, c._st, mode);
          const dayMs = c.claim_started_at_ms || c.claimed_at_ms || msOf(c.released_at);
          const statementKiosk = c._m && devById[c._m] ? c._m : null;
          const day = dayMs ? ymdOfMs(dayMs) : null;
          return (
            <>
              <button type="button" className="x" ref={closeRef} onClick={() => setDrawerOn(false)} aria-label="Close">✕</button>
              <div className="eyebrow" style={{ paddingRight: 40 }}>{KIND[b.kind][0]} · {b.title}</div>
              <div className="dcode">{c.code}</div>
              <span className={`pill ${sc}`} style={{ marginTop: 8 }}>{sl}</span>
              {b.fromHighmark && <span className="pill t-hm" style={{ marginTop: 8, marginLeft: 6 }}>From HighMark</span>}
              <div className="facts">
                <div className="fact"><div className="l">Gives</div><div className="v">{b.typeText}</div></div>
                <div className="fact"><div className="l">Works on</div><div className="v">{b.scope}</div></div>
                <div className="fact"><div className="l">Used by</div><div className="v">{c.used_by_name || '—'}</div></div>
                <div className="fact"><div className="l">Who pays</div><div className="v">{b.payer}</div></div>
              </div>
              <div className="seclabel" style={{ marginBottom: 8 }}>History</div>
              <div className="tl">
                {ev.map(([col, w, t, d], i) => (
                  <div className="ev" key={i} style={{ '--ev': col }}>
                    <div className="when">{w}</div><div className="what">{t}</div>{d ? <div className="det">{d}</div> : null}
                  </div>
                ))}
              </div>
              {platform && statementKiosk && day && (
                <button type="button" className="link" onClick={() => openStatement(statementKiosk, day, day)} data-testid="kvm-drawer-statement">
                  Open in the billing statement for {fmtDay(day)} →
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
                <button type="button" className="btn" onClick={() => copyText(c.code, `Copied ${c.code}`)}>Copy code</button>
                {!b.readOnly && READY.has(c._st) && (
                  <button type="button" className="btn danger" onClick={() => retireCode(c)} data-testid="kvm-drawer-retire">Retire this code</button>
                )}
              </div>
            </>
          );
        })()}
      </aside>

      {confirm && (
        <div className="confirm" role="dialog" aria-modal="true" aria-label={confirm.title} onClick={() => !busy && setConfirm(null)}>
          <div className="card" onClick={e => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p className="mutedtxt" style={{ fontSize: 13, margin: '0 0 16px' }}>{confirm.body}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" className="btn" onClick={() => setConfirm(null)} disabled={busy}>Keep them</button>
              <button type="button" className="btn danger" onClick={runConfirm} disabled={busy} data-testid="kvm-confirm-yes">{busy ? 'Working…' : confirm.label}</button>
            </div>
          </div>
        </div>
      )}

      {platform && giveTrial && (
        <GiveTrialModal mint={generateKioskVouchers} devices={wizardDevices} onClose={() => setGiveTrial(false)} onDone={() => { setGiveTrial(false); load(true); }} />
      )}
      {wizard && (
        <KioskVoucherWizard mode={mode} devices={wizardDevices} mint={platform ? generateKioskVouchers : mintTenantKioskVouchers}
          onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(true); }} />
      )}
    </div>
  );
}
